import {
  COMPLETED_STATUS,
  RUNNING_STATUS,
  THINKING_STAGE_LABELS,
  hasBlockedStatus,
  hasCancelledStatus,
  hasFailedStatus,
  hasPartialStatus,
  hasRunningStatus,
  hasWaitingUserStatus,
  normalizeStatus,
} from './thinkingStatus.js';
import { hasWorkflowRunProgress } from './workflowRunProcessLines.js';
import {
  resolveThinkingProcessLines,
  resolveThinkingProcessResourceEntries,
} from './thinkingProcessLines.js';
import { hasAuthoritativeWorkflowState, workflowProgressProjection, workflowRunState } from './workflowRunProjectionState.js';

export { hasFailedStatus as isThinkingFailed };
export { hasBlockedStatus as isThinkingBlocked };
export { hasCancelledStatus as isThinkingCancelled };
export { hasPartialStatus as isThinkingPartial };
export { resolveThinkingProcessLines, resolveThinkingProcessResourceEntries };

export function shouldKeepThinkingVisible({
  item,
  loading = false,
}) {
  if (!item || item.type !== 'thinking') {
    return false;
  }
  return [
    loading,
    item.loading,
    item.agentStatus,
    item.phase,
    item.thinkingTrace,
    item.executionTrace,
    hasWorkflowRunProgress(item),
  ].some(Boolean);
}

export function isThinkingWaitingUser(item) {
  return hasWaitingUserStatus(item);
}

export function isThinkingCompleted(item) {
  if (hasAuthoritativeWorkflowState(item)) {
    return COMPLETED_STATUS.has(workflowRunState(item));
  }
  const record = Object.assign({}, item);
  const agentStatus = Object.assign({}, record.agentStatus);
  return COMPLETED_STATUS.has(normalizeStatus(agentStatus.status));
}

const DEFAULT_WORKFLOW_PUBLIC_LABELS = new Set([
  'workflow pending',
  'workflow running',
  'workflow blocked',
  'workflow waiting for user',
  'workflow failed',
  'workflow completed',
  'workflow cancelled',
  'workflow status unknown',
]);

const resolveWorkflowStageLabel = (item) => {
  const status = workflowRunState(item);
  const statusLabel = THINKING_STAGE_LABELS[status] || '';
  if (status !== 'running') {
    return statusLabel;
  }
  const publicLabel = String(workflowProgressProjection(item)?.public_label || '').trim();
  return publicLabel && !DEFAULT_WORKFLOW_PUBLIC_LABELS.has(publicLabel.toLowerCase())
    ? publicLabel
    : statusLabel;
};

export function resolveThinkingStage(item, hasStreamingContent = false) {
  if (hasAuthoritativeWorkflowState(item)) {
    return resolveWorkflowStageLabel(item);
  }
  const record = Object.assign({}, item);
  const phase = Object.assign({}, record.phase);
  const agentStatusRecord = Object.assign({}, record.agentStatus);
  const phaseLabel = String(phase.label ?? '').trim();
  const phaseStatus = normalizeStatus(phase.status);
  const agentStatus = normalizeStatus(agentStatusRecord.status);
  const combinedStatus = agentStatus;
  const stageHint = normalizeStatus(agentStatusRecord.stage);
  const hintedStageLabel = THINKING_STAGE_LABELS[stageHint] ?? THINKING_STAGE_LABELS.analysis;
  const stageCandidates = [
    [hasWaitingUserStatus(item), THINKING_STAGE_LABELS.waiting_user],
    [hasFailedStatus(item), THINKING_STAGE_LABELS.failed],
    [hasBlockedStatus(item), THINKING_STAGE_LABELS.blocked],
    [hasCancelledStatus(item), THINKING_STAGE_LABELS.cancelled],
    [hasPartialStatus(item), THINKING_STAGE_LABELS.partial],
    [[Boolean(phaseLabel), !COMPLETED_STATUS.has(phaseStatus)].every(Boolean), phaseLabel],
    [hasRunningStatus(item), hintedStageLabel],
    [COMPLETED_STATUS.has(combinedStatus), THINKING_STAGE_LABELS.completed],
    [hasStreamingContent, THINKING_STAGE_LABELS.generating],
    [Boolean(THINKING_STAGE_LABELS[stageHint]), THINKING_STAGE_LABELS[stageHint]],
    [RUNNING_STATUS.has(combinedStatus), THINKING_STAGE_LABELS.analysis],
  ];
  return stageCandidates.find(([matches]) => matches)?.[1] ?? THINKING_STAGE_LABELS.analysis;
}

export function resolveThinkingTool(item) {
  if (hasAuthoritativeWorkflowState(item)) {
    return { label: '', source: '思考' };
  }
  /**
   * DOC HELPER — waiting_user display boundary.
   *
   * `waiting_user` means the assistant has already asked the next question and
   * the product is waiting for the user's confirmation. It must not display the
   * secondary "思考中" label, otherwise a settled turn looks like an infinite
   * thinking loop.
   */
  const record = Object.assign({}, item);
  const phase = Object.assign({}, record.phase);
  const hidesToolLabel = [
    hasWaitingUserStatus(item),
    isThinkingCompleted(item),
    hasPartialStatus(item),
    Boolean(String(phase.label ?? '').trim()),
  ].some(Boolean);
  if (hidesToolLabel) {
    return {
      label: '',
      source: '思考',
    };
  }
  const executionTrace = Object.assign({}, record.executionTrace);
  const agentStatus = Object.assign({}, record.agentStatus);
  const rawLabel = [executionTrace.agent, agentStatus.agent, executionTrace.step_id, agentStatus.status, '助手']
    .find(Boolean);
  const rawStepId = String(executionTrace.step_id ?? '');
  const normalizedLabel = String(rawLabel).toLowerCase();
  const labelRules = [
    { matches: [rawStepId.includes('intent_router'), normalizedLabel.includes('intent_router')], label: '识别意图' },
    { matches: [rawStepId.includes('knowledge_search'), normalizedLabel.includes('knowledge')], label: '检索证据' },
    { matches: [rawStepId.includes('requirement_collect'), normalizedLabel.includes('requirement')], label: '梳理需求' },
  ];
  const matchedRule = labelRules.find(({ matches }) => matches.some(Boolean));
  return {
    label: matchedRule?.label ?? '思考中',
    source: '思考',
  };
}
